// src/components/HereMap.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import { parseLocation } from "../../utils";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";

// Utility to load external scripts
const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

// Utility to load external CSS
const loadCSS = (href) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`link[href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    document.head.appendChild(link);
  });
};

const HereMap = ({ lawyers, isExpanded = false }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const uiRef = useRef(null);
  const markersGroupRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const outsideClickHandlerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const maxRetries = 10;
    const retryDelay = 500;
    let retryCount = 0;

    // Override HERE UI CSS that forces content-box box-sizing
    const ensureHereUiOverrides = () => {
      const styleId = 'here-ui-overrides';
      if (document.getElementById(styleId)) return;
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .H_ui { box-sizing: border-box !important; }
        .H_ui * { box-sizing: inherit; }
      `;
      document.head.appendChild(style);
    };

    const initializeMap = async () => {
      if (!isMounted) return;

      // Check if lawyers data is available
      if (!lawyers || lawyers.length === 0) {
        if (retryCount < maxRetries) {
          retryCount += 1;
          console.warn(
            `No lawyers data available. Retrying in ${retryDelay}ms... (${retryCount}/${maxRetries})`
          );
          setTimeout(initializeMap, retryDelay);
        } else {
          console.error(
            "Max retries reached. Could not initialize the map due to lack of lawyers data."
          );
        }
        return;
      }

      try {
        // Load Here Maps scripts and CSS
        await Promise.all([
          loadScript("https://js.api.here.com/v3/3.1/mapsjs-core.js"),
          loadScript("https://js.api.here.com/v3/3.1/mapsjs-service.js"),
          loadScript("https://js.api.here.com/v3/3.1/mapsjs-ui.js"),
          loadScript("https://js.api.here.com/v3/3.1/mapsjs-mapevents.js"),
          loadScript("https://js.api.here.com/v3/3.1/mapsjs-clustering.js"),
          loadCSS("https://js.api.here.com/v3/3.1/mapsjs-ui.css"),
        ]);

        // Apply CSS overrides after HERE UI stylesheet loads
        ensureHereUiOverrides();

        console.log("HERE Maps scripts loaded. Checking availability...");
        console.log("window.H:", !!window.H);
        console.log("window.H.clustering:", !!(window.H && window.H.clustering));

        if (!window.H) {
          console.error("Here Maps core not loaded properly.");
          return;
        }

        if (!window.H.clustering) {
          console.error("Here Maps clustering not loaded properly.");
          // Fallback to simple markers if clustering fails
          initSimpleMarkers();
          return;
        }

        if (mapInitializedRef.current) {
          return;
        }

        // Find the first lawyer with a valid location
        const firstValidLawyer = lawyers.find((lawyer) =>
          parseLocation(lawyer.lat, lawyer.long)
        );
        if (!firstValidLawyer) {
          console.error(
            "No valid lawyer locations available to initialize the map."
          );
          return;
        }

        const firstLawyerLocation = parseLocation(
          firstValidLawyer.lat,
          firstValidLawyer.long
        );
        if (!firstLawyerLocation) {
          console.error("First valid lawyer's location is invalid.");
          return;
        }

        console.log("Initializing platform with API key...");
        const platform = new window.H.service.Platform({
          apikey: process.env.REACT_APP_HERE_API_KEY,
        });

        const prefersDark =
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-color-scheme: dark)').matches;
        const defaultLayers = platform.createDefaultLayers({
          style: prefersDark ? 'normal.night' : 'normal.day',
        });

        console.log("Creating map...");
        mapRef.current = new window.H.Map(
          mapContainerRef.current,
          defaultLayers.vector.normal.map,
          {
            center: {
              lat: firstLawyerLocation.latitude,
              lng: firstLawyerLocation.longitude,
            },
            zoom: 12,
            pixelRatio: window.devicePixelRatio || 1,
          }
        );

        defaultLayers.vector.normal.map.setMin(3);

        // Enable map interactions
        const mapEvents = new window.H.mapevents.MapEvents(mapRef.current);
        new window.H.mapevents.Behavior(mapEvents);

        // Create default UI components
        uiRef.current = window.H.ui.UI.createDefault(
          mapRef.current,
          defaultLayers
        );

        console.log("Processing lawyers data...");
        // Process lawyers and create data points
        const validLawyers = [];
        const dataPoints = [];

        lawyers.forEach((lawyer, index) => {
          const parsedLocation = parseLocation(lawyer.lat, lawyer.long);
          if (parsedLocation) {
            console.log(`Valid lawyer ${index + 1}:`, lawyer.name, parsedLocation);
            
            const dataPoint = new window.H.clustering.DataPoint(
              parsedLocation.latitude,
              parsedLocation.longitude,
              1,
              { lawyer, index }
            );
            
            dataPoints.push(dataPoint);
            validLawyers.push({
              ...lawyer,
              parsedLocation
            });
          } else {
            console.warn(`Invalid location for lawyer:`, lawyer.name, lawyer.lat, lawyer.long);
          }
        });

        console.log(`Created ${dataPoints.length} data points from ${lawyers.length} lawyers`);

        if (dataPoints.length === 0) {
          console.error("No valid lawyer locations found.");
          return;
        }

        // Try clustering approach first
        try {
          console.log("Attempting clustering approach...");
          await initClustering(dataPoints, validLawyers);
        } catch (clusterError) {
          console.error("Clustering failed, falling back to simple markers:", clusterError);
          await initSimpleMarkers();
        }

      } catch (error) {
        console.error("Error in map initialization:", error);
        if (error.message.includes("429")) {
          alert("Map cannot be loaded at the moment due to high demand. Please try again later.");
        }
      }
    };

    // Clustering implementation
    const initClustering = async (dataPoints, validLawyers) => {
      console.log("Creating clustering provider...");
      
      const clusterProvider = new window.H.clustering.Provider(dataPoints, {
        clusteringOptions: {
          eps: 64,
          minWeight: 2,
        },
      });

      console.log("Creating custom theme...");
      
      // Simplified theme for debugging
      const customTheme = {
        getClusterPresentation: function(cluster) {
          console.log("Creating cluster marker for", cluster.getWeight(), "items");
          
          const count = cluster.getWeight();
          const icon = new window.H.map.Icon(
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
              <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#144A8C" stroke="white" stroke-width="2"/>
                <text x="20" y="25" text-anchor="middle" font-family="Arial" font-size="14" font-weight="bold" fill="white">${count}</text>
              </svg>
            `)}`,
            { size: { w: 40, h: 40 } }
          );
          
          const marker = new window.H.map.Marker(cluster.getPosition(), { icon });
          marker.setData({ type: 'cluster', cluster, count });
          
          return marker;
        },
        
        getNoisePresentation: function(noisePoint) {
          console.log("Creating single marker");
          
          const data = noisePoint.getData();
          const lawyer = data?.lawyer;
          
          if (!lawyer) {
            console.warn("No lawyer data in noise point");
            return null;
          }
          
          const icon = new window.H.map.Icon(
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
              <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#144A8C" stroke="white" stroke-width="2"/>
                <circle cx="12" cy="12" r="4" fill="white"/>
              </svg>
            `)}`,
            { size: { w: 24, h: 24 } }
          );
          
          const marker = new window.H.map.Marker(noisePoint.getPosition(), { icon });
          marker.setData({ type: 'single', lawyer });
          
          return marker;
        }
      };

      console.log("Setting theme...");
      clusterProvider.setTheme(customTheme);

      console.log("Creating layer...");
      const clusterLayer = new window.H.map.layer.ObjectLayer(clusterProvider);
      
      console.log("Adding layer to map...");
      mapRef.current.addLayer(clusterLayer);

      // Add click handlers on the map to catch taps on themed cluster/noise markers
      mapRef.current.addEventListener('tap', function(evt) {
        const target = evt.target;
        const data = target && typeof target.getData === 'function' ? target.getData() : null;

        if (!data) {
          // Clicked on map background – close any open bubbles
          if (uiRef.current) {
            uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
          }
          return;
        }

        if (data.type === 'cluster') {
          handleClusterClick(data.cluster, target.getGeometry());
        } else if (data.type === 'single') {
          handleSingleLawyerClick(data.lawyer, target.getGeometry());
        }
      });

      // Adjust view
      adjustMapView(validLawyers);
      
      // Mark as initialized
      mapInitializedRef.current = true;
      setIsInitialized(true);
      console.log("Clustering initialization complete");
    };

    // Fallback simple markers implementation
    const initSimpleMarkers = async () => {
      console.log("Initializing simple markers fallback...");
      
      if (!mapRef.current) {
        console.error("Map not available for simple markers");
        return;
      }

      // Create a group for markers
      const group = new window.H.map.Group();
      markersGroupRef.current = group;

      const validLawyers = [];
      
      lawyers.forEach((lawyer, index) => {
        const parsedLocation = parseLocation(lawyer.lat, lawyer.long);
        if (parsedLocation) {
          console.log(`Creating simple marker for:`, lawyer.name);
          
          // Create simple marker
          const marker = new window.H.map.Marker({
            lat: parsedLocation.latitude,
            lng: parsedLocation.longitude
          });

          // Set popup data
          marker.setData(createSimplePopupContent(lawyer));
          
          group.addObject(marker);
          validLawyers.push({ ...lawyer, parsedLocation });
        }
      });

      // Add group to map
      mapRef.current.addObject(group);

      // Add click handler for simple markers
      mapRef.current.addEventListener('tap', function(evt) {
        const target = evt.target;
        if (target instanceof window.H.map.Marker) {
          const bubble = new window.H.ui.InfoBubble(target.getGeometry(), {
            content: target.getData()
          });
          uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
          uiRef.current.addBubble(bubble);
          requestAnimationFrame(() => styleInfoBubbleChrome());
        } else {
          // Clicked on map background – close any open bubbles
          if (uiRef.current) {
            uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
          }
        }
      });

      console.log(`Created ${validLawyers.length} simple markers`);
      
      // Adjust view
      adjustMapView(validLawyers);
      
      // Mark as initialized
      mapInitializedRef.current = true;
      setIsInitialized(true);
      console.log("Simple markers initialization complete");
    };

    // Helper functions
    const adjustMapView = (validLawyers) => {
      if (validLawyers.length === 0) return;
      
      if (validLawyers.length === 1) {
        mapRef.current.setCenter({
          lat: validLawyers[0].parsedLocation.latitude,
          lng: validLawyers[0].parsedLocation.longitude
        });
        mapRef.current.setZoom(14);
      } else {
        const lats = validLawyers.map(l => l.parsedLocation.latitude);
        const lngs = validLawyers.map(l => l.parsedLocation.longitude);
        
        const bounds = new window.H.geo.Rect(
          Math.max(...lats),
          Math.min(...lngs),
          Math.min(...lats),
          Math.max(...lngs)
        );
        
        mapRef.current.getViewModel().setLookAtData({ bounds });
      }
    };

    const handleClusterClick = (cluster, position) => {
      const lawyers = [];
      cluster.forEachDataPoint((dataPoint) => {
        const data = dataPoint.getData();
        if (data?.lawyer) {
          lawyers.push(data.lawyer);
        }
      });
      
      if (lawyers.length === 0) return;
      
      const content = createClusterPopupContent(lawyers);
      const bubble = new window.H.ui.InfoBubble(position, { content });
      
      uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
      uiRef.current.addBubble(bubble);
      // Style HERE InfoBubble chrome so our content can fill, and keep it in view
      requestAnimationFrame(() => {
        styleInfoBubbleChrome();
        ensureBubbleInView();
        installOutsideClickCloser();
      });
    };

    const handleSingleLawyerClick = (lawyer, position) => {
      const content = createSingleLawyerPopupContent(lawyer);
      const bubble = new window.H.ui.InfoBubble(position, { content });
      
      uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
      uiRef.current.addBubble(bubble);
      requestAnimationFrame(() => {
        styleInfoBubbleChrome();
        ensureBubbleInView();
        installOutsideClickCloser();
      });
    };

    // Ensure the HERE bubble container has no extra padding that causes horizontal scroll
    const styleInfoBubbleChrome = () => {
      try {
        const bubbleContents = document.querySelectorAll('.H_ib_content');
        const el = bubbleContents[bubbleContents.length - 1];
        if (el) {
          el.style.padding = '0';
          el.style.boxSizing = 'border-box';
          el.style.width = '480px';
          const body = el.closest('.H_ib_body');
          if (body) {
            body.style.boxSizing = 'border-box';
          }
        }
      } catch (_) {
        // noop
      }
    };

    // Pan the map so the bubble is fully visible (top in view, horizontally centered)
    // const ensureBubbleInView = () => {
    //   try {
    //     const mapEl = mapContainerRef.current;
    //     const bodies = document.querySelectorAll('.H_ib_body');
    //     const body = bodies[bodies.length - 1];
    //     if (!mapEl || !body || !mapRef.current) return;

    //     const mapRect = mapEl.getBoundingClientRect();
    //     const bubbleRect = body.getBoundingClientRect();
    //     const margin = 12;

    //     const currentCenter = mapRef.current.getCenter();
    //     const centerScreen = mapRef.current.geoToScreen(currentCenter);

    //     const viewCenterX = mapRect.left + mapRect.width / 2;
    //     const bubbleCenterX = bubbleRect.left + bubbleRect.width / 2;
    //     const desiredCenterXForBubble = viewCenterX; // horizontally center the bubble
    //     const deltaX = bubbleCenterX - desiredCenterXForBubble;

    //     // Vertically: keep top in view (margin) and roughly center
    //     const topLimit = mapRect.top + margin;
    //     const desiredBubbleCenterY = Math.max(
    //       topLimit + bubbleRect.height / 2,
    //       mapRect.top + mapRect.height / 2 // never push above viewport center if very tall
    //     );
    //     const bubbleCenterY = bubbleRect.top + bubbleRect.height / 2;
    //     const deltaY = bubbleCenterY - desiredBubbleCenterY;

    //     if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

    //     // Move the map center in the SAME direction as the delta, so content moves opposite
    //     // and the bubble shifts toward the viewport center
    //     const targetScreenX = centerScreen.x + (deltaX * 0.5);
    //     const targetScreenY = centerScreen.y + (deltaY * 0.5);
    //     const targetGeo = mapRef.current.screenToGeo(targetScreenX, targetScreenY);
    //     mapRef.current.getViewModel().setLookAtData({ position: targetGeo }, true);
    //   } catch (_) {
    //     // noop
    //   }
    // };



    const ensureBubbleInView = ({ anchorGeo } = {}) => {
      try {
        const map = mapRef.current;
        if (!map) {
          console.warn("No mapRef.current found");
          return;
        }
    
        const vpEl = map.getViewPort().element;
        if (!vpEl) {
          console.warn("No map viewport element found");
          return;
        }
    
        console.log("=== ensureBubbleInView START ===");
    
        // Defer so bubble layout is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const bodies = document.querySelectorAll('.H_ib_body');
            const body = bodies[bodies.length - 1];
            if (!body) {
              console.warn("No bubble body found");
              return;
            }
    
            const vpRect = vpEl.getBoundingClientRect();
            const bubRect = body.getBoundingClientRect();
    
            console.log("Viewport rect:", vpRect);
            console.log("Bubble rect (viewport coords):", bubRect);
    
            if (!bubRect.width || !bubRect.height) {
              console.warn("Bubble has no size");
              return;
            }
    
            // Convert bubble rect to MAP-LOCAL coords
            const bubble = {
              left:   bubRect.left   - vpRect.left,
              right:  bubRect.right  - vpRect.left,
              top:    bubRect.top    - vpRect.top,
              bottom: bubRect.bottom - vpRect.top,
              width:  bubRect.width,
              height: bubRect.height
            };
    
            console.log("Bubble rect (map-local coords):", bubble);
    
            const mapW = vpRect.width;
            const mapH = vpRect.height;
            const PAD = { top: 20, right: 20, bottom: 20, left: 20 };
    
            let dx = 0, dy = 0;
    
            if (bubble.left < PAD.left) {
              dx = PAD.left - bubble.left;
            } else if (bubble.right > mapW - PAD.right) {
              dx = (mapW - PAD.right) - bubble.right;
            }
    
            if (bubble.top < PAD.top) {
              dy = PAD.top - bubble.top;
            } else if (bubble.bottom > mapH - PAD.bottom) {
              dy = (mapH - PAD.bottom) - bubble.bottom;
            }
    
            console.log("Initial dx, dy (bubble inside check):", dx, dy);
    
            // Optional: clamp for anchor
            if (anchorGeo) {
              const a = map.geoToScreen(anchorGeo);
              console.log("Anchor screen coords (map-local):", a);
    
              const ANCHOR_PAD = 40;
              const minX = ANCHOR_PAD, maxX = mapW - ANCHOR_PAD;
              const minY = ANCHOR_PAD, maxY = mapH - ANCHOR_PAD;
    
              const dxMin = a.x - maxX;
              const dxMax = a.x - minX;
              dx = Math.max(dxMin, Math.min(dxMax, dx));
    
              const dyMin = a.y - maxY;
              const dyMax = a.y - minY;
              dy = Math.max(dyMin, Math.min(dyMax, dy));
    
              console.log("Clamped dx, dy (anchor visibility):", dx, dy);
            }
    
            if (dx === 0 && dy === 0) {
              console.log("No movement required (dx/dy are zero)");
              return;
            }
    
            const vm = map.getViewModel();
            try { vm.setLookAtData(vm.getLookAtData(), false); } catch (_) {}
    
            const center = map.getCenter();
            const cs = map.geoToScreen(center);
            console.log("Map center geo:", center, "screen coords:", cs);
    
            const newCenter = map.screenToGeo(cs.x - dx, cs.y - dy);
            console.log("New center geo:", newCenter);
    
            if (!newCenter || isNaN(newCenter.lat) || isNaN(newCenter.lng)) {
              console.warn("Invalid new center calculated");
              return;
            }
    
            vm.setLookAtData({ position: newCenter }, true);
    
            console.log("=== ensureBubbleInView END ===");
          });
        });
      } catch (e) {
        console.warn("ensureBubbleInView error", e);
      }
    };
    


    const installOutsideClickCloser = () => {
      try {
        if (outsideClickHandlerRef.current) {
          document.removeEventListener('pointerdown', outsideClickHandlerRef.current, true);
        }
        outsideClickHandlerRef.current = (ev) => {
          const bodies = document.querySelectorAll('.H_ib_body');
          const body = bodies[bodies.length - 1];
          if (!body) return;
          if (body.contains(ev.target)) return;
          if (uiRef.current) {
            uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
          }
          document.removeEventListener('pointerdown', outsideClickHandlerRef.current, true);
          outsideClickHandlerRef.current = null;
        };
        document.addEventListener('pointerdown', outsideClickHandlerRef.current, true);
      } catch (_) {
        // noop
      }
    };

    const createClusterPopupContent = (lawyers) => {
      // Render plain content sized to the bubble; bubble chrome provides the frame
      const root = document.createElement('div');
      root.style.cssText = `
        box-sizing: border-box;
        width: 480px;
        font-family: Poppins, -apple-system, system-ui, Arial, sans-serif;
        background: transparent;
      `;

      const header = document.createElement('div');
      header.style.cssText = `
        padding: 16px 20px;
        border-bottom: 1px solid #e5e7eb;
        font-weight: 700;
        font-size: 18px;
        color: #111827;
      `;
      header.textContent = `${lawyers.length} Provider${lawyers.length > 1 ? 's' : ''}`;

      const list = document.createElement('div');
      list.style.cssText = `
        box-sizing: border-box;
        max-height: 420px;
        overflow-y: auto;
      `;

      lawyers.forEach((lawyer, index) => {
        const item = createLawyerListItem(lawyer, index < lawyers.length - 1);
        list.appendChild(item);
      });

      root.appendChild(header);
      root.appendChild(list);
      return root;
    };

    const createSingleLawyerPopupContent = (lawyer) => {
      const root = document.createElement('div');
      root.style.cssText = `
        box-sizing: border-box;
        width: 480px;
        font-family: Poppins, -apple-system, system-ui, Arial, sans-serif;
        background: transparent;
      `;

      const item = createLawyerListItem(lawyer, false);
      root.appendChild(item);
      return root;
    };

    const createSimplePopupContent = (lawyer) => {
      const specialties = (lawyer.practice_areas || [])
        .map((area) => `<li>${getPracticeAreaLabel(area)}</li>`)
        .join("");
      
      return `
        <div style="font-family: Poppins, -apple-system, system-ui, Arial, sans-serif; width: 300px; background: transparent;">
          <div style="display:flex; gap:10px; align-items:center;">
            <img src="${lawyer.profile_photo_url || ""}" alt="${lawyer.name || "Lawyer"}" 
                 style="width:56px; height:56px; border-radius:50%; object-fit:cover;" />
            <div>
              <div style="font-weight:700;">${lawyer.name || ""}</div>
              <div style="color:#5f6368; font-size:12px;">${lawyer.firm_name || ""}</div>
            </div>
          </div>
          <div style="margin:8px 0; font-size:12px;"><strong>Experience:</strong> ${lawyer.years_of_experience || 0} years</div>
          <div style="font-size:12px;"><strong>Specialty:</strong></div>
          <ul style="padding-left:18px; margin:4px 0 8px 0; font-size:12px; max-height:90px; overflow:auto;">${specialties}</ul>
          <a href="/lawyer-detail/${lawyer.user_id}" style="display:inline-block; padding:8px 10px; background:#144A8C; color:#fff; font-weight:700; text-decoration:none; border-radius:6px;">View profile and reviews</a>
        </div>
      `;
    };

    const createLawyerListItem = (lawyer, showBorder) => {
      const item = document.createElement('div');
      item.style.cssText = `
        display: block;
        box-sizing: border-box;
        padding: 16px 20px;
        ${showBorder ? 'border-bottom: 1px solid #f3f4f6;' : ''}
        text-decoration: none;
        color: inherit;
        background: #fff;
      `;

      // Top row: avatar + content
      const topRow = document.createElement('div');
      topRow.style.cssText = `
        display: flex;
        gap: 16px;
        align-items: center;
      `;

      const img = document.createElement('img');
      img.src = lawyer.profile_photo_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Im0xMiAxMmMxLjY1NSAwIDMtMS4zNDUgMy0zcy0xLjM0NS0zLTMtMy0zIDEuMzQ1LTMgM3MxLjM0NSAzIDMgM3ptMCAyYy0yLjIxIDAtNC0xLjc5LTQtNHMxLjc5LTQgNC00IDQgMS43OSA0IDRzLTEuNzkgNC00IDR6IiBmaWxsPSIjOTQ5NUE0Ii8+Cjwvc3ZnPgo8L3N2Zz4K';
      img.alt = lawyer.name || '';
      img.style.cssText = `
        width: 56px;
        height: 56px;
        border-radius: 50%;
        object-fit: cover;
        background: #f3f4f6;
        flex: 0 0 56px;
      `;

      const content = document.createElement('div');
      content.style.cssText = 'flex: 1; min-width: 0;';

      const name = document.createElement('div');
      name.style.cssText = `
        font-weight: 700;
        font-size: 18px;
        color: #111827;
        line-height: 1.25;
      `;
      name.textContent = lawyer.name || 'Unknown Lawyer';

      const subtitle = document.createElement('div');
      subtitle.style.cssText = `
        font-size: 14px;
        color: #6b7280;
        margin-top: 4px;
      `;
      const primaryArea = (lawyer.practice_areas && lawyer.practice_areas.length > 0)
        ? getPracticeAreaLabel(lawyer.practice_areas[0])
        : '';
      subtitle.textContent = primaryArea || lawyer.firm_name || '';

      // Meta rows (rating, distance) if present
      const metaContainer = document.createElement('div');
      metaContainer.style.cssText = 'margin-top: 8px; display: grid; gap: 8px;';

      const ratingValue = lawyer.rating || lawyer.average_rating;
      const reviewsCount = lawyer.review_count || lawyer.total_reviews;
      if (ratingValue) {
        const ratingRow = document.createElement('div');
        ratingRow.style.cssText = 'display:flex; align-items:center; gap:8px; color:#111827; font-size:14px;';
        ratingRow.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 20 20" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.802 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.802-2.034a1 1 0 00-1.175 0l-2.802 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
          <span>${Number(ratingValue).toFixed(2)}${reviewsCount ? ` · ${reviewsCount} reviews` : ''}</span>`;
        metaContainer.appendChild(ratingRow);
      }

      const distanceMiles = lawyer.distance_miles || lawyer.distance;
      if (distanceMiles) {
        const distanceRow = document.createElement('div');
        distanceRow.style.cssText = 'display:flex; align-items:center; gap:8px; color:#111827; font-size:14px;';
        distanceRow.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 20 20" fill="#6B7280" xmlns="http://www.w3.org/2000/svg"><path d="M10 2a6 6 0 00-6 6c0 4.418 6 10 6 10s6-5.582 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"/></svg>
          <span>${Number(distanceMiles).toFixed(1)} mi</span>`;
        metaContainer.appendChild(distanceRow);
      }

      content.appendChild(name);
      content.appendChild(subtitle);
      content.appendChild(metaContainer);

      topRow.appendChild(img);
      topRow.appendChild(content);
      item.appendChild(topRow);

      // Buttons stacked full width
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: grid;
        gap: 12px;
        margin-top: 16px;
      `;

      const bookButton = document.createElement('a');
      bookButton.href = `/lawyer-detail/${lawyer.user_id}`;
      bookButton.style.cssText = `
        display: inline-block;
        text-align: center;
        width: 100%;
        background: #144A8C;
        color: #ffffff;
        padding: 14px 20px;
        border-radius: 8px;
        text-decoration: none;
        font-size: 16px;
        font-weight: 700;
      `;
      bookButton.textContent = 'Book an appointment';

      const viewButton = document.createElement('a');
      viewButton.href = `/lawyer-detail/${lawyer.user_id}`;
      viewButton.style.cssText = `
        display: inline-block;
        text-align: center;
        width: 100%;
        color: #144A8C;
        background: #ffffff;
        border: 2px solid #e5e7eb;
        padding: 14px 20px;
        border-radius: 8px;
        text-decoration: none;
        font-size: 16px;
        font-weight: 600;
      `;
      viewButton.textContent = 'View profile and reviews';

      buttonContainer.appendChild(bookButton);
      buttonContainer.appendChild(viewButton);
      item.appendChild(buttonContainer);

      return item;
    };

    const debouncedInitializeMap = debounce(initializeMap, 300);
    debouncedInitializeMap();

    return () => {
      isMounted = false;
    };
  }, [lawyers]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      try {
        if (mapRef.current) {
          mapRef.current.getViewPort().resize();
        }
      } catch (e) {
        // noop
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: isExpanded ? "100vh" : "500px",
        position: "relative",
      }}
      className="map-area"
    >
      {!isInitialized && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <p>Loading map...</p>
        </div>
      )}
    </div>
  );
};

HereMap.propTypes = {
  lawyers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      practice_areas: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  isExpanded: PropTypes.bool,
};

export default HereMap;